import { AlertSeverity, AlertStatus, AlertOwner } from '@cbm/shared';
import { AlertTriangle, Clock, CheckCircle, User, Wrench } from 'lucide-react';

interface AlertStatusBadgeProps {
  status: AlertStatus;
  className?: string;
}

const STATUS_STYLES = {
  OPEN: 'bg-red-100 text-red-800 border-red-200',
  ACKNOWLEDGED: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  CLOSED: 'bg-green-100 text-green-800 border-green-200',
};

const STATUS_ICONS = {
  OPEN: AlertTriangle,
  ACKNOWLEDGED: Clock,
  CLOSED: CheckCircle,
};

export function AlertStatusBadge({ status, className = '' }: AlertStatusBadgeProps) {
  const Icon = STATUS_ICONS[status];
  
  return (
    <span className={`
      inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border
      ${STATUS_STYLES[status]} ${className}
    `}>
      <Icon className="w-3 h-3 mr-1" />
      {status.toLowerCase()}
    </span>
  );
}

interface AlertSeverityBadgeProps {
  severity: AlertSeverity;
  className?: string;
}

const SEVERITY_STYLES = {
  EARLY: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  MID: 'bg-orange-100 text-orange-800 border-orange-200',
  LATE: 'bg-red-100 text-red-800 border-red-200',
};

export function AlertSeverityBadge({ severity, className = '' }: AlertSeverityBadgeProps) {
  return (
    <span className={`
      inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border
      ${SEVERITY_STYLES[severity]} ${className}
    `}>
      {severity.toLowerCase()}
    </span>
  );
}

interface AlertOwnerBadgeProps {
  owner: AlertOwner;
  className?: string;
}

const OWNER_STYLES = {
  PLANT: 'bg-blue-100 text-blue-800 border-blue-200',
  OEM: 'bg-purple-100 text-purple-800 border-purple-200',
};

const OWNER_ICONS = {
  PLANT: User,
  OEM: Wrench,
};

export function AlertOwnerBadge({ owner, className = '' }: AlertOwnerBadgeProps) {
  const Icon = OWNER_ICONS[owner];
  
  return (
    <span className={`
      inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border
      ${OWNER_STYLES[owner]} ${className}
    `}>
      <Icon className="w-3 h-3 mr-1" />
      {owner}
    </span>
  );
}

// Combined badge component
interface AlertBadgesProps {
  severity: AlertSeverity;
  status: AlertStatus;
  owner: AlertOwner;
  className?: string;
}

export function AlertBadges({ severity, status, owner, className = '' }: AlertBadgesProps) {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <AlertSeverityBadge severity={severity} />
      <AlertStatusBadge status={status} />
      <AlertOwnerBadge owner={owner} />
    </div>
  );
}